import { useMemo, useRef, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
    ArrayUnique,
    IsArray,
    IsBoolean,
    IsNotEmpty,
    IsOptional,
    IsString,
    MaxLength,
    MinLength,
} from 'class-validator';
import { useTranslation } from 'react-i18next';
import { FieldError, UseFormReturn } from 'react-hook-form';

import Form from 'src/components/FormComponents/Form';
import Input, {
    Error as InputError,
    Label as InputLabel,
} from 'src/components/FormComponents/Input';
import { ConsumerGroupsSelect } from 'src/components/FormComponents/ConsumerGroupsSelect';
import CheckBox from 'src/components/FormComponents/CheckBox';
import { useNotification } from 'src/components/Notification/useNotfication';
import container from 'src/container';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import i18n from 'src/i18n';
import { CreateMessageDto } from 'src/domain/message';
import { OrganizationGroup, OrganizationType } from 'src/domain/organization';
import { useConsumers } from 'src/view/hooks';
import { buildLeafLabelSuffixByGroupId } from 'src/utils/consumerLabelUtils';
import { useNextOfKins } from 'src/view/hooks/nextOfKin';
import { getHttpErrorMessage } from 'src/utils/httpErrorMessage';
import { Alert } from 'src/components/Alert';
import { Link } from 'react-router-dom';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import {
    getPrimaryRole,
    isOrganizationAdministrator,
    UserRole,
} from 'src/domain/user';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { useCreatorRoleContext } from 'src/view/hooks/useCreatorRoleContext';
import { MessageAttachmentsField } from './MessageAttachmentsField';
import {
    MessageAttachmentConversionError,
    uploadMessageAttachments,
} from './messageAttachmentUpload';
import { isTargetScopedToSingleResident } from './messageCommentScope';

const __validatorDecorators = {
    ArrayUnique,
    IsArray,
    IsBoolean,
    IsNotEmpty,
    IsOptional,
    IsString,
    MaxLength,
    MinLength,
};
void __validatorDecorators;

const {
    cradle: { munduClient, storageService },
} = container;

class CreateMessageFormValues {
    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    organizationId!: string;

    @IsString({ message: () => i18n.t('validation.requiredField') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    subject = '';

    @IsString({ message: () => i18n.t('validation.requiredField') })
    body = '';

    @IsOptional()
    @IsArray()
    @ArrayUnique()
    @IsString({ each: true })
    groupIds?: string[];

    @IsOptional()
    @IsBoolean()
    sendToAll?: boolean;

    @IsOptional()
    @IsBoolean()
    sendToMainContacts?: boolean;
}

export type CreateMessageFormProps = {
    organizationId?: string;
    organizationName?: string;
    organizationType?: OrganizationType;
    onSuccess: () => void;
    canSendToAll?: boolean;
    allowedGroupIds?: string[];
    groupOptions?: OrganizationGroup[];
};

export const CreateMessageForm = ({
    organizationId,
    organizationName,
    organizationType,
    onSuccess,
    canSendToAll,
    allowedGroupIds,
    groupOptions,
}: CreateMessageFormProps) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const user = useSignedInUser();
    const role = getPrimaryRole(user);
    const { activeRoleGroupIds } = useCreatorRoleContext();
    const { consumers } = useConsumers();
    const { organizationId: currentOrganizationId, organization } =
        useCurrentOrganization();
    const resolvedOrganizationId =
        organizationId ?? currentOrganizationId ?? '';
    const { data: nextOfKins } = useNextOfKins({
        organizationId: resolvedOrganizationId,
    });
    const [attachments, setAttachments] = useState<File[]>([]);
    const methodsRef = useRef<UseFormReturn<CreateMessageFormValues> | null>(
        null,
    );

    const resolvedOrganizationName =
        organizationName ??
        organization?.organizationDisplayName ??
        organization?.organizationName;
    const resolvedOrganizationType = organizationType ?? organization?.type;
    const isOrganizationAdmin = isOrganizationAdministrator(user);

    const derivedAllowedGroupIds =
        role === UserRole.creator && !isOrganizationAdmin
            ? activeRoleGroupIds
            : undefined;

    const derivedGroupOptions = useMemo(() => {
        if (!organization?.groups?.length) {
            return undefined;
        }

        if (role !== UserRole.creator || isOrganizationAdmin) {
            return organization.groups;
        }

        if (!derivedAllowedGroupIds || !derivedAllowedGroupIds.length) {
            return [];
        }

        const allowedSet = new Set(derivedAllowedGroupIds);
        return organization.groups.filter(group => allowedSet.has(group.id));
    }, [
        organization?.groups,
        role,
        isOrganizationAdmin,
        derivedAllowedGroupIds,
    ]);

    const resolvedAllowedGroupIds = allowedGroupIds ?? derivedAllowedGroupIds;
    const resolvedGroupOptions = groupOptions ?? derivedGroupOptions;
    const resolvedCanSendToAll =
        canSendToAll ?? isOrganizationAdministrator(user);

    const consumersWithRoleAccess = useMemo(() => {
        if (!consumers.length) return [] as typeof consumers;

        if (role !== UserRole.creator || isOrganizationAdmin) {
            return consumers;
        }

        if (!derivedAllowedGroupIds || !derivedAllowedGroupIds.length) {
            return [] as typeof consumers;
        }

        const allowedGroups = new Set(derivedAllowedGroupIds);
        return consumers.filter(consumer =>
            (consumer.groupIds ?? []).some(groupId =>
                allowedGroups.has(groupId),
            ),
        );
    }, [consumers, role, isOrganizationAdmin, derivedAllowedGroupIds]);

    const consumersWithoutNextOfKin = useMemo(() => {
        if (!consumersWithRoleAccess.length)
            return [] as typeof consumersWithRoleAccess;
        const nextOfKinByConsumer = new Map(
            (nextOfKins ?? []).flatMap(nok =>
                (nok.followers?.consumerIds ?? []).map(
                    consumerId => [consumerId, nok] as const,
                ),
            ),
        );

        return consumersWithRoleAccess.filter(
            consumer => !nextOfKinByConsumer.has(consumer.id),
        );
    }, [consumersWithRoleAccess, nextOfKins]);

    const handleSubmit = async (payload: CreateMessageFormValues) => {
        const cleanedPayload: CreateMessageDto = {
            organizationId: payload.organizationId,
            subject: payload.subject.trim(),
            body: payload.body.trim(),
            sendToAll: resolvedCanSendToAll
                ? Boolean(payload.sendToAll)
                : false,
            groupIds: payload.groupIds?.length ? payload.groupIds : undefined,
            sendToMainContacts: Boolean(payload.sendToMainContacts),
        };

        cleanedPayload.commentsEnabled = isTargetScopedToSingleResident({
            sendToAll: cleanedPayload.sendToAll,
            groupIds: cleanedPayload.groupIds,
            consumers: consumersWithRoleAccess,
            organizationGroups: resolvedGroupOptions,
        });

        const hasTargets =
            cleanedPayload.sendToAll ||
            Boolean(cleanedPayload.groupIds?.length);

        if (!hasTargets) {
            notify(t('modules.messages.form.targetsRequired'), true);
            return;
        }

        try {
            let attachmentsPayload: CreateMessageDto['attachments'];
            let attachmentsContainerName: string | undefined;

            if (attachments.length) {
                const sas = await munduClient.getMessageMediaSas();
                attachmentsContainerName = sas.containerName;

                attachmentsPayload = await uploadMessageAttachments(
                    attachments,
                    sas,
                    storageService,
                );
            }

            await munduClient.createMessage({
                ...cleanedPayload,
                attachments: attachmentsPayload,
                attachmentsContainerName,
            });
            notify(t('modules.messages.notifications.created'));
            methodsRef.current?.reset({
                organizationId: resolvedOrganizationId,
                subject: '',
                body: '',
                sendToAll: false,
                sendToMainContacts: false,
                groupIds: [],
            });
            setAttachments([]);
            onSuccess();
        } catch (error) {
            if (error instanceof MessageAttachmentConversionError) {
                notify(
                    t('modules.messages.form.attachmentConversionFailed'),
                    true,
                );
                return;
            }

            const message = await getHttpErrorMessage(
                error,
                t('admin.notifications.genericError'),
            );
            notify(message, true);
        }
    };

    return (
        <Form<CreateMessageFormValues>
            schema={CreateMessageFormValues}
            onSubmit={handleSubmit}
            mode="onChange"
            defaultValues={{
                organizationId: resolvedOrganizationId,
                subject: '',
                body: '',
                sendToAll: false,
                sendToMainContacts: false,
                groupIds: [],
            }}
        >
            {methods => {
                methodsRef.current = methods;
                const {
                    register,
                    control,
                    watch,
                    formState: { errors, isSubmitting },
                } = methods;

                const sendToAll = resolvedCanSendToAll
                    ? watch('sendToAll')
                    : false;
                const sendToMainContacts = watch('sendToMainContacts');
                const selectedGroupIds = watch('groupIds') ?? [];
                const isNursingHome =
                    resolvedOrganizationType === OrganizationType.nursingHome;
                const sendToAllLabel = resolvedOrganizationName
                    ? t('modules.messages.form.sendToAllLabelWithOrg', {
                          organizationName: resolvedOrganizationName,
                      })
                    : t('modules.messages.form.sendToAllLabel');
                const displayedGroupOptions =
                    sendToAll && organization?.groups?.length
                        ? organization.groups
                        : resolvedGroupOptions;
                const displayedAllowedGroupIds = sendToAll
                    ? undefined
                    : resolvedAllowedGroupIds;

                const emptySelectedGroups = (() => {
                    if (!displayedGroupOptions?.length) return [] as string[];

                    const allowedSet = displayedAllowedGroupIds?.length
                        ? new Set(displayedAllowedGroupIds)
                        : undefined;

                    const groupsToCheck = allowedSet
                        ? displayedGroupOptions.filter(group =>
                              allowedSet.has(group.id),
                          )
                        : displayedGroupOptions;

                    const targetGroupIds = sendToAll
                        ? groupsToCheck.map(group => group.id)
                        : selectedGroupIds;

                    if (!groupsToCheck.length || !targetGroupIds.length)
                        return [] as string[];

                    const parentIdsWithChildren = new Set(
                        displayedGroupOptions
                            .map(group => group.parentId)
                            .filter((id): id is string => Boolean(id)),
                    );

                    const childrenByParent = groupsToCheck.reduce<
                        Record<string, string[]>
                    >((acc, group) => {
                        if (group.parentId) {
                            acc[group.parentId] = acc[group.parentId] || [];
                            acc[group.parentId].push(group.id);
                        }
                        return acc;
                    }, {});

                    const expandedTargetGroupIds = new Set<string>();
                    const stack = [...targetGroupIds];

                    while (stack.length) {
                        const current = stack.pop();
                        if (!current || expandedTargetGroupIds.has(current)) {
                            continue;
                        }
                        expandedTargetGroupIds.add(current);
                        const children = childrenByParent[current] || [];
                        children.forEach(child => stack.push(child));
                    }

                    const leafGroups = groupsToCheck.filter(
                        group =>
                            !parentIdsWithChildren.has(group.id) &&
                            expandedTargetGroupIds.has(group.id),
                    );

                    const consumersByGroupId = new Set(
                        consumers.flatMap(consumer => consumer.groupIds ?? []),
                    );

                    const groupsById = new Map(
                        displayedGroupOptions.map(
                            group => [group.id, group] as const,
                        ),
                    );

                    return leafGroups
                        .filter(group => !consumersByGroupId.has(group.id))
                        .map(group => {
                            const parent = group.parentId
                                ? groupsById.get(group.parentId)
                                : undefined;
                            const parentLabel = parent?.name || parent?.id;
                            const leafLabel = group.name || group.id;

                            return parentLabel
                                ? `${parentLabel} | ${leafLabel}`
                                : leafLabel;
                        });
                })();
                const emptySelectedGroupsLabel = emptySelectedGroups.join(', ');
                const emptyGroupsContactName =
                    organization?.profile.firstName ?? '';
                const emptyGroupsContactEmail = organization?.email ?? '';

                const leafLabelSuffixByGroupId =
                    buildLeafLabelSuffixByGroupId(consumers, nextOfKins, {
                        mainContactsOnly: Boolean(sendToMainContacts),
                    }) ?? {};

                return (
                    <div className="flex flex-col gap-4">
                        <input
                            type="hidden"
                            value={resolvedOrganizationId}
                            {...register('organizationId')}
                        />
                        <InputLabel
                            label={t('modules.messages.form.recipientsTitle')}
                            className="mb-0"
                        />
                        {resolvedCanSendToAll && (
                            <CheckBox
                                label={sendToAllLabel}
                                disabled={isSubmitting}
                                {...register('sendToAll')}
                            />
                        )}
                        {isNursingHome && (
                            <CheckBox
                                label={t(
                                    'modules.messages.form.sendToMainContactsLabel',
                                )}
                                disabled={isSubmitting}
                                {...register('sendToMainContacts')}
                            />
                        )}
                        <ConsumerGroupsSelect
                            control={control}
                            name="groupIds"
                            organizationId={resolvedOrganizationId}
                            groups={displayedGroupOptions}
                            allSelected={sendToAll}
                            allowedGroupIds={displayedAllowedGroupIds}
                            leafLabelSuffixByGroupId={leafLabelSuffixByGroupId}
                            error={
                                Array.isArray(errors.groupIds)
                                    ? (errors.groupIds[0] as FieldError)
                                    : (errors.groupIds as
                                          | FieldError
                                          | undefined)
                            }
                            disabled={sendToAll}
                        />
                        {emptySelectedGroups.length > 0 && (
                            <Alert
                                message={
                                    <>
                                        {t(
                                            'modules.messages.form.emptyGroupsWarning',
                                            {
                                                groups:
                                                    emptySelectedGroupsLabel.length >
                                                    100
                                                        ? `${emptySelectedGroupsLabel.slice(
                                                              0,
                                                              100,
                                                          )}...`
                                                        : emptySelectedGroupsLabel,
                                            },
                                        )}{' '}
                                        {isOrganizationAdministrator(user) ? (
                                            <Link
                                                to={{
                                                    pathname: '/app/users',
                                                    search: 'sort=name:asc',
                                                }}
                                                className="underline hover:opacity-80"
                                            >
                                                {t(
                                                    'modules.messages.form.emptyGroupsCta',
                                                )}
                                            </Link>
                                        ) : (
                                            <span>
                                                {t(
                                                    'modules.messages.form.emptyGroupsCtaNonOrg',
                                                    {
                                                        name: emptyGroupsContactName,
                                                        email: emptyGroupsContactEmail,
                                                    },
                                                )}
                                            </span>
                                        )}
                                    </>
                                }
                                isSuccess={false}
                                canClose={false}
                                className="text-sm"
                            />
                        )}
                        {resolvedOrganizationType ===
                            OrganizationType.nursingHome &&
                            consumersWithoutNextOfKin.length > 0 && (
                                <Alert
                                    message={
                                        <>
                                            {t(
                                                'modules.messages.form.noNextOfKinWarning',
                                                {
                                                    count: consumersWithoutNextOfKin.length,
                                                },
                                            )}{' '}
                                            <Link
                                                to={{
                                                    pathname: '/app/users',
                                                    search: 'sort=mainContacts:asc',
                                                    state: {
                                                        highlightUsersWithoutNextOfKin:
                                                            true,
                                                    },
                                                }}
                                                className="underline hover:opacity-80"
                                            >
                                                {t(
                                                    'modules.messages.form.noNextOfKinCta',
                                                )}
                                            </Link>
                                        </>
                                    }
                                    isSuccess={false}
                                    canClose={false}
                                    className="text-sm"
                                />
                            )}
                        <Input
                            className="text-input"
                            label={t('modules.messages.form.subjectLabel')}
                            error={errors.subject}
                            {...register('subject')}
                        />
                        <div>
                            <InputLabel
                                label={t('modules.messages.form.bodyLabel')}
                            />
                            <textarea
                                className="text-input w-full min-h-[160px]"
                                {...register('body')}
                            />
                            <InputError error={errors.body} />
                        </div>
                        <MessageAttachmentsField
                            attachments={attachments}
                            onChange={setAttachments}
                            disabled={isSubmitting}
                        />
                        <Input
                            type="submit"
                            value={t('modules.messages.form.submit')}
                            className="primary-btn-small self-end"
                            disabled={isSubmitting}
                        />
                    </div>
                );
            }}
        </Form>
    );
};
